import { useCallback } from "react";
import { getRetailerRedemptions } from "@/api/client";
import { CsvDownloadButton } from "@/components/CsvDownloadButton";
import { DataTable } from "@/components/DataTable";
import { MultiFilterSelect } from "@/components/MultiFilterSelect";
import { storeCampaignRedemptionColumns } from "@/components/redemptionColumns";
import { TimeSeriesAreaChart } from "@/components/TimeSeriesAreaChart";
import { ChartSkeleton, ErrorState, TableSkeleton } from "@/components/states";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useApiData } from "@/hooks/useApiData";
import { useProductFilterOptions } from "@/hooks/useProductFilters";
import { useUrlListFilter } from "@/hooks/useUrlFilters";

export function RetailerRedemptionsPage() {
  const filters = useProductFilterOptions();
  const [storeIds, setStoreIds, store] = useUrlListFilter("store");

  const fetchRedemptions = useCallback(
    (token: string) => getRetailerRedemptions(token, { storeId: store }),
    [store],
  );
  const state = useApiData(fetchRedemptions);
  const data = state.status === "ready" ? state.data : null;

  return (
    <div className="flex flex-col gap-6" aria-busy={state.status === "loading"}>
      <div>
        <h1 className="text-xl font-semibold">Retailer redemptions</h1>
        <p className="text-sm text-muted-foreground">
          Redemptions at each store and what it earned, per campaign, last 6
          months.
        </p>
      </div>

      <MultiFilterSelect
        label="Store"
        allLabel="All stores"
        values={storeIds}
        onChange={setStoreIds}
        disabled={filters.disabled}
        options={filters.storeOptions}
      />

      {state.status === "error" ? (
        <ErrorState message={state.message} onRetry={state.reload} />
      ) : (
        <>
          <Card>
            <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
              <div className="space-y-1.5">
                <CardTitle>Redemptions by campaign</CardTitle>
                <CardDescription>
                  Redemption count and store earnings for each store-campaign
                  pair
                </CardDescription>
              </div>
              {data ? (
                <CsvDownloadButton
                  name="retailer-redemptions"
                  columns={storeCampaignRedemptionColumns}
                  rows={data.redemptionsByCampaign}
                />
              ) : null}
            </CardHeader>
            <CardContent>
              {data ? (
                <DataTable
                  columns={storeCampaignRedemptionColumns}
                  rows={data.redemptionsByCampaign}
                  rowKey={(r, i) => `${r.storeName}-${r.campaignName}-${i}`}
                  maxHeight="32rem"
                />
              ) : (
                <TableSkeleton />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Redemptions over time</CardTitle>
              <CardDescription>Daily redemptions</CardDescription>
            </CardHeader>
            <CardContent>
              {!data ? (
                <ChartSkeleton height={260} />
              ) : (
                <TimeSeriesAreaChart
                  data={data.redemptionsOverTime.map((d) => ({
                    day: d.day,
                    value: d.redemptions,
                  }))}
                  seriesName="Redemptions"
                />
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
